import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/layout/Header";
import { LayoutDashboard, Search, Calendar, ChevronDown, UserPlus, Users, Check, Redo2, Edit, Trash } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek } from "date-fns";
import { StudentDetails } from "@/components/nurse/StudentDetails";
import { StaffDetails } from "@/components/nurse/StaffDetails";
import { StudentForm } from "@/components/nurse/StudentForm";
import { EmployeeForm } from "@/components/nurse/EmployeeForm";
import { useToast } from "@/hooks/use-toast";
import { useConnection } from "@/contexts/ConnectionContext";
import { useOffline } from "@/contexts/OfflineContext";
import { CommunityVisitDialog } from "@/components/nurse/CommunityVisitDialog";
import { StudentVisitDialog } from "@/components/nurse/StudentVisitDialog";
import { StaffVisitDialog } from "@/components/nurse/StaffVisitDialog";
import { CommunityMember, Student } from "@/types";
import { EditStaffDialog } from "@/components/staff/EditStaffDialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

export default function NurseDashboard() {
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const {
    isSupabaseConnected,
    isOnline
  } = useConnection();
  const {
    isInitialized: isOfflineReady,
    getCachedData
  } = useOffline();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);
  const [isFollowUpOpen, setIsFollowUpOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddEmployeeForm, setShowAddEmployeeForm] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [staffResults, setStaffResults] = useState<any[]>([]);
  const [communityResults, setCommunityResults] = useState<any[]>([]);
  const [showDetails, setShowDetails] = useState(false);
  const [communityVisitOpen, setCommunityVisitOpen] = useState(false);
  const [selectedCommunityMember, setSelectedCommunityMember] = useState<any>(null);
  const [studentDialogOpen, setStudentDialogOpen] = useState(false);
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [communityDialogOpen, setCommunityDialogOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [editingStaff, setEditingStaff] = useState<CommunityMember | null>(null);
  const [isEditStaffDialogOpen, setIsEditStaffDialogOpen] = useState(false);

  const {
    data: nurseProfile
  } = useQuery({
    queryKey: ['nurseProfile', user?.id],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('profiles').select('id').eq('user_id', user?.id).single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id
  });

  const {
    data: currentPeriod
  } = useQuery({
    queryKey: ['currentPeriod'],
    queryFn: async () => {
      console.log('Fetching current period...');
      const {
        data: activeYear,
        error: yearError
      } = await supabase.from('academic_years').select('*, terms(*)').eq('is_active', true).single();
      if (yearError) {
        console.error('Error fetching academic year:', yearError);
        throw yearError;
      }
      const activeTerm = activeYear?.terms?.find((term: any) => term.is_active);
      console.log('Current period fetched:', {
        activeYear,
        activeTerm
      });
      return {
        academicYear: activeYear,
        term: activeTerm
      };
    }
  });

  const {
    data: visitStats = {
      todayVisits: 0,
      weekVisits: 0,
      totalStudents: 0,
      termVisits: 0
    },
    refetch: refetchVisitStats
  } = useQuery({
    queryKey: ['visitStats', user?.schoolId, currentPeriod?.academicYear?.id, currentPeriod?.term?.id],
    queryFn: async () => {
      if (!user?.schoolId || !currentPeriod?.academicYear?.id || !currentPeriod?.term?.id) {
        return {
          todayVisits: 0,
          weekVisits: 0,
          totalStudents: 0,
          termVisits: 0
        };
      }
      
      const today = startOfDay(new Date());
      const weekStart = startOfWeek(new Date());
      
      try {
        const {
          count: todayCount,
          error: todayError
        } = await supabase.from('visits').select('*', {
          count: 'exact',
          head: true
        }).eq('school_id', user.schoolId).eq('academic_year_id', currentPeriod.academicYear.id).eq('term_id', currentPeriod.term.id).gte('date', today.toISOString()).lte('date', endOfDay(today).toISOString());
        
        if (todayError) throw todayError;
        
        const {
          count: weekCount,
          error: weekError
        } = await supabase.from('visits').select('*', {
          count: 'exact',
          head: true
        }).eq('school_id', user.schoolId).eq('academic_year_id', currentPeriod.academicYear.id).eq('term_id', currentPeriod.term.id).gte('date', weekStart.toISOString()).lte('date', endOfWeek(weekStart).toISOString());
        
        if (weekError) throw weekError;
        
        const {
          count: studentCount,
          error: studentError
        } = await supabase.from('students').select('*', {
          count: 'exact',
          head: true
        }).eq('school_id', user.schoolId).eq('is_archived', false);
        
        if (studentError) throw studentError;
        
        const {
          count: termCount,
          error: termError
        } = await supabase.from('visits').select('*', {
          count: 'exact',
          head: true
        }).eq('school_id', user.schoolId).eq('academic_year_id', currentPeriod.academicYear.id).eq('term_id', currentPeriod.term.id);
        
        if (termError) throw termError;
        
        return {
          todayVisits: todayCount || 0,
          weekVisits: weekCount || 0,
          totalStudents: studentCount || 0,
          termVisits: termCount || 0
        };
      } catch (error) {
        console.error('Error fetching visit stats:', error);
        throw error;
      }
    },
    enabled: !!user?.schoolId && !!currentPeriod?.academicYear?.id && !!currentPeriod?.term?.id,
    staleTime: 0,
    refetchInterval: 1000
  });

  const handleVisitSaved = () => {
    console.log('Visit saved, updating counts optimistically...');
    queryClient.setQueryData(['visitStats', user?.schoolId, currentPeriod?.academicYear?.id, currentPeriod?.term?.id], (old: any) => {
      const newStats = {
        ...old,
        todayVisits: (old?.todayVisits || 0) + 1,
        weekVisits: (old?.weekVisits || 0) + 1
      };
      console.log('Updated stats:', newStats);
      return newStats;
    });
    setTimeout(() => {
      console.log('Refetching stats to verify...');
      refetchVisitStats();
    }, 1000);
  };

  useEffect(() => {
    if (!user?.schoolId) return;
    const channel = supabase.channel('visit-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'visits',
      filter: `school_id=eq.${user.schoolId}`
    }, () => {
      refetchVisitStats();
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.schoolId, refetchVisitStats]);

  const {
    data: followUpStudents = []
  } = useQuery({
    queryKey: ['followUpVisits', user?.schoolId],
    queryFn: async () => {
      if (!user?.schoolId) return [];
      if (!isOnline && isOfflineReady) {
        console.log('Fetching offline follow-up visits...');
        const visits = await getCachedData('visits');
        const students = await getCachedData('students');
        const communityMembers = await getCachedData('community_members');
        return visits.filter((visit: any) => visit.school_id === user.schoolId && visit.requires_followup).map((visit: any) => ({
          ...visit,
          student: students.find((s: any) => s.id === visit.student_id),
          community_member: communityMembers.find((cm: any) => cm.id === visit.community_member_id)
        })).sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
      }
      console.log('Fetching online follow-up visits...');
      const {
        data,
        error
      } = await supabase.from('visits').select(`
          id,
          date,
          complaint,
          treatment,
          students (
            id,
            surname,
            other_names,
            class,
            stream
          ),
          community_members (
            id,
            name,
            category
          )
        `).eq('school_id', user.schoolId).eq('requires_followup', true).order('date', {
        ascending: false
      });
      if (error) {
        console.error('Error fetching follow-up visits:', error);
        return [];
      }
      return data.map(visit => ({
        ...visit,
        student: visit.students,
        community_member: visit.community_members
      }));
    },
    enabled: !!user?.schoolId && (isSupabaseConnected || isOfflineReady && !isOnline)
  });

  const handleCompleteFollowUp = async (visitId: string) => {
    try {
      const {
        error
      } = await supabase.from('visits').update({
        requires_followup: false
      }).eq('id', visitId);
      if (error) throw error;
      toast({
        title: "Success",
        description: "Follow-up marked as complete"
      });
      queryClient.invalidateQueries({
        queryKey: ['followUpVisits']
      });
    } catch (error) {
      console.error('Error completing follow-up:', error);
      toast({
        title: "Error",
        description: "Failed to complete follow-up",
        variant: "destructive"
      });
    }
  };

  const handleTreatAgain = (student: Student) => {
    setSelectedStudent(student);
  };

  const handleDelete = async (student: Student) => {
    try {
      const {
        error
      } = await supabase.from('students').delete().eq('id', student.id);
      if (error) throw error;
      toast({
        title: "Success",
        description: "Student deleted successfully"
      });
      setSearchTerm("");
      setSearchResults([]);
      queryClient.invalidateQueries({
        queryKey: ['students']
      });
    } catch (error) {
      console.error('Error deleting student:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete student",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    const searchPeople = async () => {
      if (!searchTerm) {
        setSearchResults([]);
        setStaffResults([]);
        setCommunityResults([]);
        setSelectedStaff(null);
        setIsSearching(false);
        return;
      }
      console.log('Starting search with term:', searchTerm);
      setIsSearching(true);
      try {
        if (!isOnline && isOfflineReady) {
          console.log('Performing offline search...');
          const students = await getCachedData('students');
          const communityMembers = await getCachedData('community_members');
          const filteredStudents = students.filter((student: any) => (student.surname?.toLowerCase().includes(searchTerm.toLowerCase()) || student.other_names?.toLowerCase().includes(searchTerm.toLowerCase())) && student.school_id === user?.schoolId && !student.is_archived);
          const filteredCommunityMembers = communityMembers.filter((member: any) => member.name?.toLowerCase().includes(searchTerm.toLowerCase()) && member.school_id === user?.schoolId);
          setSearchResults(filteredStudents);
          setCommunityResults(filteredCommunityMembers);
        } else if (isSupabaseConnected) {
          const {
            data: studentData,
            error: studentError
          } = await supabase.from('students').select('*').eq('school_id', user?.schoolId).eq('is_archived', false).or(`surname.ilike.%${searchTerm}%,other_names.ilike.%${searchTerm}%`).order('surname');
          if (studentError) throw studentError;
          const {
            data: communityData,
            error: communityError
          } = await supabase.from('community_members').select('*').eq('school_id', user?.schoolId).ilike('name', `%${searchTerm}%`).order('name');
          if (communityError) throw communityError;
          setSearchResults(studentData || []);
          setCommunityResults(communityData || []);
        }
      } catch (error) {
        console.error('Error in search process:', error);
        toast({
          title: "Search Error",
          description: "Please try refreshing the page if this persists",
          variant: "destructive"
        });
        setSearchResults([]);
        setCommunityResults([]);
      } finally {
        setIsSearching(false);
      }
    };
    const timeoutId = setTimeout(searchPeople, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, user?.schoolId, toast, isSupabaseConnected, isOnline, isOfflineReady, getCachedData]);

  const handleStaffSelect = (staff: any) => {
    console.log('Selected staff:', staff);
    setSelectedStaff(staff);
    setShowDetails(true);
    setSearchTerm("");
  };

  const handleEditStaff = (member: CommunityMember, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingStaff(member);
    setIsEditStaffDialogOpen(true);
  };

  const handleDeleteStaff = async (member: CommunityMember, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm(`Are you sure you want to delete ${member.name}?`)) {
      try {
        const {
          error
        } = await supabase.from("community_members").delete().eq("id", member.id);
        if (error) throw error;
        toast({
          title: "Success",
          description: "Staff member deleted successfully"
        });
        setCommunityResults(prev => prev.filter(item => item.id !== member.id));
      } catch (error) {
        console.error("Error deleting staff member:", error);
        toast({
          title: "Error",
          description: "Failed to delete staff member",
          variant: "destructive"
        });
      }
    }
  };

  return <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container px-4 py-6 mt-[147px]">
        <div className="space-y-6">
          {!showDetails ? <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <LayoutDashboard className="h-6 w-6 text-primary" />
                  <h1 className="text-2xl font-bold my-[13px]">Nurse Dashboard</h1>
                  {!isOnline && isOfflineReady && <span className="ml-2 text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                      Offline Mode
                    </span>}
                </div>

                <div className="flex items-center gap-4">
                  <Button onClick={() => setShowAddEmployeeForm(true)}>
                    <Users className="mr-2 h-4 w-4" />
                    Add Employee
                  </Button>
                  <Button onClick={() => setShowAddForm(true)}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    Add Student
                  </Button>

                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {currentPeriod?.academicYear?.name} {currentPeriod?.term?.name ? `(${currentPeriod.term.name})` : ''}
                      {currentPeriod?.term?.name && <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                          {currentPeriod.term.name} Active
                        </span>}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input placeholder="Search students or community members..." className="pl-10" value={searchTerm} onChange={e => {
                console.log('Search input changed:', e.target.value);
                setSearchTerm(e.target.value);
              }} />
                  {searchTerm && <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg">
                      {isSearching ? <div className="p-4 text-center text-muted-foreground">
                          Searching...
                        </div> : !isSupabaseConnected ? <div className="p-4 text-center text-muted-foreground">
                          Reconnecting to database...
                        </div> : searchResults.length > 0 || communityResults.length > 0 ? <>
                          {searchResults.length > 0 && <div>
                              <div className="px-2 py-1 text-sm font-semibold text-muted-foreground bg-muted">
                                Students
                              </div>
                              {searchResults.map(student => <div key={student.id} className="p-2 hover:bg-[#F2FCE2] cursor-pointer">
                                  <div className="flex items-center justify-between">
                                    <div onClick={() => {
                          setSelectedStudent(student);
                          setShowDetails(true);
                          setSearchTerm("");
                        }}>
                                      <div className="font-medium">
                                        {student.surname}
                                        {student.other_names ? `, ${student.other_names}` : ''}
                                      </div>
                                      <div className="text-sm text-muted-foreground">
                                        Class {student.class}
                                        {student.stream ? ` ${student.stream}` : ''}
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <Button variant="ghost" size="icon" onClick={e => {
                            e.stopPropagation();
                            setEditingStudent(student);
                            setShowAddForm(true);
                            setSearchTerm("");
                          }}>
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button variant="ghost" size="icon" onClick={e => {
                            e.stopPropagation();
                            handleDelete(student);
                          }}>
                                        <Trash className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>)}
                            </div>}
                          {communityResults.length > 0 && <div>
                              <div className="px-2 py-1 text-sm font-semibold text-muted-foreground bg-muted">
                                Staff
                              </div>
                              {communityResults.map(member => <div key={member.id} className="p-2 hover:bg-[#F2FCE2] cursor-pointer">
                                  <div className="flex items-center justify-between">
                                    <div onClick={() => {
                          setCommunityVisitOpen(true);
                          setSelectedCommunityMember(member);
                          setSearchTerm("");
                        }}>
                                      <div className="font-medium">
                                        {member.name}
                                      </div>
                                      <div className="text-sm text-muted-foreground">
                                        {member.category.replace(/_/g, ' ')}
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                      <Button variant="ghost" size="icon" onClick={e => handleEditStaff(member, e)}>
                                        <Edit className="h-4 w-4" />
                                      </Button>
                                      <Button variant="ghost" size="icon" onClick={e => handleDeleteStaff(member, e)}>
                                        <Trash className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>)}
                            </div>}
                        </> : <div className="p-4 text-center text-muted-foreground">
                          No results found
                        </div>}
                    </div>}
                </div>
              </div>

              <div className="space-y-4">
                <Collapsible open={isFollowUpOpen} onOpenChange={setIsFollowUpOpen}>
                  <CollapsibleTrigger className="flex w-full items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold">Follow-up Required</h2>
                      <span className="rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                        {followUpStudents.length}
                      </span>
                    </div>
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isFollowUpOpen ? 'rotate-180' : ''}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="mt-4 space-y-4">
                      {followUpStudents.map(visit => <Collapsible key={visit.id}>
                          <Card>
                            <CardContent className="p-4">
                              <CollapsibleTrigger className="flex w-full items-start justify-between">
                                <div className="space-y-1">
                                  <div className="font-medium">
                                    {visit.student?.surname}
                                    {visit.student?.other_names ? `, ${visit.student.other_names}` : ""}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    Class {visit.student?.class}
                                    {visit.student?.stream ? ` ${visit.student.stream}` : ""}
                                  </div>
                                  <div className="text-sm text-muted-foreground">
                                    {format(new Date(visit.date), 'PPp')}
                                  </div>
                                </div>
                                <ChevronDown className="h-4 w-4 transition-transform duration-200" />
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <div className="mt-4">
                                  <div className="mt-2">
                                    <div className="text-sm font-medium">Complaint</div>
                                    <div className="text-sm text-muted-foreground">{visit.complaint}</div>
                                  </div>
                                  <div className="mt-2">
                                    <div className="text-sm font-medium">Treatment</div>
                                    <div className="text-sm text-muted-foreground">{visit.treatment}</div>
                                  </div>
                                  <div className="mt-4 flex gap-2 justify-end">
                                    <Button variant="outline" size="sm" onClick={() => handleCompleteFollowUp(visit.id)} className="flex items-center gap-1">
                                      <Check className="h-4 w-4" />
                                      Complete
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => handleTreatAgain(visit.student)} className="flex items-center gap-1">
                                      <Redo2 className="h-4 w-4" />
                                      Treat Again
                                    </Button>
                                  </div>
                                </div>
                              </CollapsibleContent>
                            </CardContent>
                          </Card>
                        </Collapsible>)}
                      {followUpStudents.length === 0 && <div className="text-center text-muted-foreground py-4">
                          No follow-ups required
                        </div>}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>

              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{visitStats.todayVisits}</div>
                    <p className="text-sm text-muted-foreground">Today's Visits</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{visitStats.weekVisits}</div>
                    <p className="text-sm text-muted-foreground">This Week's Visits</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{visitStats.termVisits}</div>
                    <p className="text-sm text-muted-foreground">
                      {currentPeriod?.term?.name ? `${currentPeriod.term.name} Visits` : "Term Visits"}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">{visitStats.totalStudents}</div>
                    <p className="text-sm text-muted-foreground">Total Students</p>
                  </CardContent>
                </Card>
              </div>
            </> : selectedStudent ? <StudentDetails student={selectedStudent} onBack={() => {
          setSelectedStudent(null);
          setShowDetails(false);
          setSearchTerm("");
        }} onVisitSaved={handleVisitSaved} /> : null}
        </div>
      </main>

      {showAddForm && <StudentForm student={editingStudent} onClose={() => {
      setShowAddForm(false);
      setEditingStudent(null);
    }} />}

      {showAddEmployeeForm && <EmployeeForm onClose={() => setShowAddEmployeeForm(false)} />}

      <CommunityVisitDialog open={communityVisitOpen} onOpenChange={setCommunityVisitOpen} member={selectedCommunityMember} onVisitSaved={handleVisitSaved} />

      <StudentVisitDialog student={selectedStudent} open={studentDialogOpen} onOpenChange={setStudentDialogOpen} onVisitSaved={handleVisitSaved} />

      <StaffVisitDialog staff={selectedStaff} open={staffDialogOpen} onOpenChange={setStaffDialogOpen} onVisitSaved={handleVisitSaved} />

      {isEditStaffDialogOpen && editingStaff && <EditStaffDialog open={isEditStaffDialogOpen} onOpenChange={open => {
      setIsEditStaffDialogOpen(open);
      if (!open) {
        if (searchTerm) {
          const searchPeople = async () => {
            try {
              const {
                data: communityData,
                error
              } = await supabase.from('community_members').select('*').eq('school_id', user?.schoolId).ilike('name', `%${searchTerm}%`).order('name');
              if (error) throw error;
              setCommunityResults(communityData || []);
            } catch (error) {
              console.error('Error refreshing community results:', error);
            }
          };
          searchPeople();
        }
        setEditingStaff(null);
      }
    }} staffMember={editingStaff} />}
    </div>;
}
